import { useAuth } from "@/composables/useAuth";

function base64UrlDecode(input: string): string {
  let payload = input.replace(/-/g, "+").replace(/_/g, "/");
  while (payload.length % 4) payload += "=";
  return atob(payload);
}

function decodeJwtToObject(token: string): Record<string, unknown> | null {
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const json = base64UrlDecode(parts[1]);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

// Environment-driven checks removed: this helper no longer reads runtime/build env vars.

interface SmokeResult {
    ok: boolean;
    details: string[];
    decoded: Record<string, unknown> | null;
}
export interface SmokeOpts {
    expectedIss?: string;
    expectedAud?: string;
    expectedScope?: string;
}

export async function runDevelopmentSmokeTest(opts?: SmokeOpts): Promise<string> {
  const { getApiToken } = useAuth();
  const result: SmokeResult = { ok: true, details: [], decoded: null };

  let token: string | null = null;
  try {
    token = await getApiToken();
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    result.ok = false;
    result.details.push(`Error acquiring token: ${msg}`);
    return JSON.stringify(result, null, 2);
  }

  if (!token) {
    result.ok = false;
    result.details.push("No token acquired");
    return JSON.stringify(result, null, 2);
  }

  const payload = decodeJwtToObject(token);
  if (!payload) {
    result.ok = false;
    result.details.push("Failed to decode JWT payload");
    result.decoded = null;
    return JSON.stringify(result, null, 2);
  }
  result.decoded = payload;
  result.details.push("Token decoded");

  const now = Math.floor(Date.now() / 1000);
  const exp = payload.exp ? Number(payload.exp) : undefined;
  const nbf = payload.nbf ? Number(payload.nbf) : undefined;
  if (exp !== undefined && !Number.isNaN(exp) && exp <= now) {
    result.ok = false;
    result.details.push(`Token expired (exp=${exp})`);
  }
  if (nbf !== undefined && !Number.isNaN(nbf) && nbf > now) {
    result.ok = false;
    result.details.push(`Token not yet valid (nbf=${nbf})`);
  }

  // configurable issuer/audience/scope checks (only run when provided via opts)
  const expectedIss = opts?.expectedIss;
  const expectedAud = opts?.expectedAud;
  const expectedScope = opts?.expectedScope;

  let anyChecks = false;
  if (expectedIss) {
    anyChecks = true;
    const iss = typeof payload.iss === "string" ? payload.iss : "";
    const issOk = iss === expectedIss || iss.startsWith(expectedIss + "/") || iss.startsWith(expectedIss);
    if (!issOk) {
      result.ok = false;
      result.details.push(`iss mismatch: got=${iss} expected=${expectedIss}`);
    } else {
      result.details.push(`iss ok: ${expectedIss}`);
    }
  }

  if (expectedAud) {
    anyChecks = true;
    const audMatches = (() => {
      if (Array.isArray(payload.aud)) return payload.aud.includes(expectedAud);
      if (typeof payload.aud === "string") return payload.aud === expectedAud;
      if (payload.azp) return payload.azp === expectedAud;
      return false;
    })();
    if (!audMatches) {
      result.ok = false;
      result.details.push(`aud/azp mismatch: aud=${JSON.stringify(payload.aud)} azp=${payload.azp} expected=${expectedAud}`);
    } else {
      result.details.push(`aud/azp ok: ${expectedAud}`);
    }
  }

  if (expectedScope) {
    anyChecks = true;
    const scpStr = typeof payload.scp === "string" ? payload.scp as string : "";
    const scpList = scpStr ? scpStr.split(/\s+/) : [];
    const roles = Array.isArray(payload.roles) ? (payload.roles as string[]) : [];

    const shortExpected = expectedScope.split("/").pop() ?? expectedScope;
    const candidates = Array.from(new Set([expectedScope, shortExpected]));

    const hasScope = candidates.some((c) => scpList.includes(c) || roles.includes(c));
    if (!hasScope) {
      result.ok = false;
      result.details.push(`Missing expected scope/role: ${expectedScope}`);
    } else {
      result.details.push(`scope/role ok: ${expectedScope}`);
    }
  }

  if (!anyChecks) {
    result.details.push("Issuer/audience/scope checks skipped (no expected values passed)");
  }

  // API probe removed; keep smoke test limited to token validation

  return JSON.stringify(result, null, 2);
}
